"use client";

import { useCallback, useSyncExternalStore } from "react";

type SetStepParameter = (
  stepId: string | null,
  options?: { replace?: boolean },
) => void;

// `history.pushState` fires no event, so every lead flow on the page hears
// about another flow's step change through these listeners; Back and Forward
// arrive as `popstate`.
const NAVIGATION_STATE_KEY = "leadFlowNavigation";

/**
 * Which flow's Continue pushed this history entry, if any, and how many of
 * that flow's pushes in a row lead up to it. One owner rather than a flag per
 * flow: a flag copied forward into the next entry would let one flow's Back
 * pop another flow's step.
 */
type LeadFlowNavigationState = {
  lastPushedBy: string | null;
  pushedStepCount: number;
};

type LeadFlowHistoryState = {
  [NAVIGATION_STATE_KEY]?: LeadFlowNavigationState;
};

const listeners = new Set<() => void>();

function readHistoryState(): LeadFlowHistoryState {
  const state: unknown = window.history.state;

  if (typeof state === "object" && state !== null && !Array.isArray(state)) {
    return state as LeadFlowHistoryState;
  }

  return {};
}

/**
 * How many history entries this flow's Continue pushed in a row, ending at
 * the current one. In-form Back walks a pushed entry; a deep link, a replaced
 * step, or another flow's push was not, so Back replaces it instead.
 */
function countPushedSteps(parameterName: string): number {
  const navigationState = readHistoryState()[NAVIGATION_STATE_KEY];

  return navigationState?.lastPushedBy === parameterName
    ? navigationState.pushedStepCount
    : 0;
}

let isHistoryBackPending = false;

/**
 * Walks back `entryCount` history entries and reports whether it started.
 * `popstate` arrives asynchronously, so until it does the step parameter
 * still shows the step being left; another walk in that window would go past
 * the intended entry and could leave the page.
 */
function goBackInHistory(entryCount = 1, onSettled?: () => void): boolean {
  if (isHistoryBackPending) {
    return false;
  }

  isHistoryBackPending = true;
  window.addEventListener(
    "popstate",
    () => {
      isHistoryBackPending = false;
      onSettled?.();
    },
    { once: true },
  );
  window.history.go(-entryCount);
  return true;
}

/**
 * Next.js skips syncing its router for a history write whose state carries
 * its own markers, so copying them along would leave the router on the old
 * URL and its next commit, such as a server action's refresh, would push
 * that URL back. Its patched history methods copy what they need themselves.
 *
 * `__NA` and `_N` are Next.js internals, set in
 * `next/dist/client/components/app-router.js` rather than a public API, so
 * re-check them on every Next.js upgrade.
 */
const NEXT_HISTORY_MARKER_KEYS = ["__NA", "_N"];

function withNavigationState(
  lastPushedBy: string | null,
): LeadFlowHistoryState {
  const historyState: LeadFlowHistoryState = Object.fromEntries(
    Object.entries(readHistoryState()).filter(
      ([key]) => !NEXT_HISTORY_MARKER_KEYS.includes(key),
    ),
  );

  return {
    ...historyState,
    [NAVIGATION_STATE_KEY]: {
      lastPushedBy,
      pushedStepCount:
        lastPushedBy === null ? 0 : countPushedSteps(lastPushedBy) + 1,
    },
  };
}

function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("popstate", listener);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

function getServerStepParameter(): null {
  return null;
}

/** The flow's step parameter in the URL right now, outside of render. */
function readStepParameter(parameterName: string): string | null {
  return new URLSearchParams(window.location.search).get(parameterName);
}

/**
 * Reads and writes one lead flow's step query parameter. Writes use the
 * native history API, which Next.js syncs into its router without a server
 * round trip. The server snapshot is always empty, so a deep-linked step
 * applies after hydration and the server renders the first step.
 */
function useStepParameter(
  parameterName: string,
): [string | null, SetStepParameter] {
  const stepParameter = useSyncExternalStore(
    subscribe,
    () => readStepParameter(parameterName),
    getServerStepParameter,
  );

  const setStepParameter = useCallback<SetStepParameter>(
    (stepId, { replace = false } = {}) => {
      const url = new URL(window.location.href);
      if (stepId === null) {
        url.searchParams.delete(parameterName);
      } else {
        url.searchParams.set(parameterName, stepId);
      }

      if (url.href === window.location.href) {
        return;
      }

      const historyState = withNavigationState(replace ? null : parameterName);

      if (replace) {
        window.history.replaceState(historyState, "", url);
      } else {
        window.history.pushState(historyState, "", url);
      }
      notifyListeners();
    },
    [parameterName],
  );

  return [stepParameter, setStepParameter];
}

export {
  countPushedSteps,
  goBackInHistory,
  readStepParameter,
  useStepParameter,
};
